import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { AccountsAPI, TagsAPI } from "../../../utils/firebase";
import { getUserTransactionsCached } from "../../../utils/firebase/cache/transactionsCached";
import styles from "./GlobalSearchInput.module.css";

const RECENTS_KEY = "pp:search:recents";
const HINT_KEY = "pp:search:hintSeen";
const MAX_RECENTS = 8;
const DEBOUNCE_MS = 180;

function loadRecents() {
  try {
    const arr = JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]");
    return Array.isArray(arr) ? arr.slice(0, MAX_RECENTS) : [];
  } catch { return []; }
}
function saveRecents(list) {
  try { localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, MAX_RECENTS))); } catch {}
}
function pushRecent(q) {
  const clean = String(q || "").trim();
  if (!clean) return;
  const cur = loadRecents();
  const dedup = [clean, ...cur.filter(x => x.toLowerCase() !== clean.toLowerCase())];
  saveRecents(dedup);
}
function markHintSeen() {
  try { localStorage.setItem(HINT_KEY, "1"); } catch {}
}
function hintSeen() {
  try { return localStorage.getItem(HINT_KEY) === "1"; } catch { return true; }
}

export default function GlobalSearchInput() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  const email = (user?.email || "").trim();

  // query + ui state
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(-1);
  const [showHint, setShowHint] = useState(!hintSeen());

  // data (cache-first, subscribe for accounts/tags)
  const [accounts, setAccounts] = useState([]);
  const [tags, setTags] = useState([]);
  const [txCache, setTxCache] = useState([]);

  // recents
  const [recents, setRecents] = useState(loadRecents());

  // ——— subscriptions (accounts/tags)
  useEffect(() => {
    if (!email) return;
    let unsubA = () => {}, unsubT = () => {};
    try {
      unsubA = AccountsAPI.subscribeUserAccounts({
        userEmail: email, cap: 1000,
        onData: rows => setAccounts(rows || []),
        onError: () => setAccounts([]),
      });
    } catch {}
    try {
      unsubT = TagsAPI.subscribeUserTags({
        userEmail: email, cap: 2000,
        onData: docs => setTags((docs || []).map(d => String(d.fullPath || d.name || "").trim()).filter(Boolean)),
        onError: () => setTags([]),
      });
    } catch {}
    return () => { try { unsubA && unsubA(); } catch {}; try { unsubT && unsubT(); } catch {}; };
  }, [email]);

  // ——— load transactions (cache only; no network while typing)
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!email) return;
      try {
        const { rows } = await getUserTransactionsCached({
          userEmail: email,
          latestFirst: true,
          cap: 5000,            // keep it snappy
          ttlMs: 60 * 60 * 1000 // 1h
        });
        if (!alive) return;
        setTxCache(Array.isArray(rows) ? rows : []);
      } catch {/* ignore */}
    })();
    return () => { alive = false; };
  }, [email]);

  // ——— outside click closes
  useEffect(() => {
    function onDoc(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // ——— debounce input
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  // ——— build sections
  const sections = useMemo(() => {
    const term = debouncedQ.toLowerCase();
    const out = [];

    // 1) Recents — only when box is focused, open, and query is empty
    if (open && !term && recents.length) {
      out.push({
        key: "recents",
        title: "Recent searches",
        items: recents.map(r => ({ type: "query", id: `recent:${r}`, label: r }))
      });
    }

    if (!term) return out;

    // util: simple match helpers
    const starts = (s) => s.toLowerCase().startsWith(term);
    const contains = (s) => s.toLowerCase().includes(term);

    // 2) Accounts
    const accItems = [];
    for (const a of accounts || []) {
      const name = String(a?.name || "").trim();
      if (!name) continue;
      if (starts(name) || contains(name)) {
        accItems.push({ type: "account", id: `acc:${a.id || name}`, label: name });
        if (accItems.length >= 6) break;
      }
    }
    if (accItems.length) out.push({ key: "accounts", title: "Accounts", items: accItems });

    // 3) Tags
    const tagItems = [];
    for (const t of tags || []) {
      if (starts(t) || contains(t)) {
        tagItems.push({ type: "tag", id: `tag:${t}`, label: t });
        if (tagItems.length >= 8) break;
      }
    }
    if (tagItems.length) out.push({ key: "tags", title: "Tags", items: tagItems });

    // 4) Transactions (description)
    const txItems = [];
    for (const r of txCache || []) {
      const d = String(r?.description || "").trim();
      if (!d) continue;
      if (starts(d) || contains(d)) {
        txItems.push({
          type: "query",
          id: `tx:${r.id || d + Math.random()}`,
          label: d,
          meta: r.date || ""
        });
      }
      if (txItems.length >= 6) break;
    }
    if (txItems.length) out.push({ key: "transactions", title: "Transactions", items: txItems });

    return out;
  }, [debouncedQ, open, accounts, tags, txCache, recents]);

  // flatten for keyboard nav
  const flatItems = useMemo(() => {
    const arr = [];
    for (const s of sections) {
      for (const it of s.items) arr.push(it);
    }
    // add "see all" pseudo-item at end when q present
    if (debouncedQ) {
      arr.push({ type: "seeall", id: "__seeall__", label: `See all results for “${debouncedQ}”` });
    }
    return arr;
  }, [sections, debouncedQ]);

  // ——— commit helpers
  const goQuery = (query) => {
    const val = String(query || "").trim();
    if (!val) return;
    pushRecent(val);
    setRecents(loadRecents());
    navigate(`/app/transactions?q=${encodeURIComponent(val)}`);
    setOpen(false);
  };
  const goAccount = (name) => {
    const val = String(name || "").trim();
    if (!val) return;
    navigate(`/app/transactions?account=${encodeURIComponent(val)}`);
    setOpen(false);
  };
  const goTag = (path) => {
    const val = String(path || "").trim();
    if (!val) return;
    navigate(`/app/transactions?tag=${encodeURIComponent(val)}`);
    setOpen(false);
  };
  const commitItem = (item) => {
    if (!item) return;
    if (item.type === "account") return goAccount(item.label);
    if (item.type === "tag") return goTag(item.label);
    if (item.type === "query") return goQuery(item.label);
    if (item.type === "seeall") return goQuery(debouncedQ || q);
  };

  // ——— keyboard
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      setHover((h) => Math.min(flatItems.length - 1, h + 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHover((h) => Math.max(-1, h - 1));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      // Enter always commits: focused item or See all
      if (hover >= 0 && flatItems[hover]) commitItem(flatItems[hover]);
      else if (q.trim()) goQuery(q.trim());
      return;
    }
    if (e.key === "Escape") {
      // ESC: first clears, second blurs (when already empty)
      if (q.trim()) {
        e.preventDefault();
        setQ("");
        setOpen(true); // show recents after clearing
        setHover(-1);
      } else {
        inputRef.current?.blur();
        setOpen(false);
      }
      return;
    }
  };

  // ——— events
  const onFocus = () => {
    if (showHint) { setShowHint(false); markHintSeen(); }
    setOpen(true);
  };
  const onChange = (e) => {
    setQ(e.target.value);
    setOpen(true);
    setHover(-1);
  };

  // ——— rendering
  return (
    <div className={styles.wrap} ref={wrapRef}>
      <div className={styles.inputWrap}>
        <input
          ref={inputRef}
          data-pp-global-search
          className={`form-control form-control-sm ${styles.input}`}
          type="search"
          placeholder="Search transactions, accounts, tags"
          value={q}
          onChange={onChange}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          autoComplete="off"
          aria-expanded={open ? "true" : "false"}
          aria-controls="pp-global-search-menu"
        />
        {showHint && (
          <span className={styles.hint} aria-hidden="true">Press /</span>
        )}
      </div>

      {open && (
        <div id="pp-global-search-menu" className={`dropdown-menu show ${styles.menu}`} role="listbox">
          {sections.length === 0 && !debouncedQ && (
            <div className={styles.empty}>Start typing…</div>
          )}

          {sections.map(section => (
            <div key={section.key} className={styles.group}>
              <div className={styles.groupLabel}>{section.title}</div>
              {section.items.map((it, idx) => {
                const i = flatItems.findIndex(f => f.id === it.id); // stable index for hover
                const active = i === hover;
                return (
                  <button
                    key={it.id}
                    type="button"
                    className={`dropdown-item ${active ? "active" : ""} ${styles.item}`}
                    onMouseEnter={() => setHover(i)}
                    onMouseLeave={() => setHover(-1)}
                    onClick={() => commitItem(it)}
                    role="option"
                    aria-selected={active ? "true" : "false"}
                    title={it.label}
                  >
                    <span className={styles.itemMain}>{it.label}</span>
                    {it.meta ? <span className={styles.itemMeta}>{it.meta}</span> : null}
                  </button>
                );
              })}
            </div>
          ))}

          {debouncedQ && (
            <>
              <div className={styles.divider} />
              <button
                type="button"
                className={`dropdown-item ${hover === flatItems.length - 1 ? "active" : ""} ${styles.seeAll}`}
                onMouseEnter={() => setHover(flatItems.length - 1)}
                onMouseLeave={() => setHover(-1)}
                onClick={() => goQuery(debouncedQ)}
                role="option"
                aria-selected={hover === flatItems.length - 1 ? "true" : "false"}
              >
                See all results for “{debouncedQ}”
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
